<template>
  <div 
    :class="['comment', (informed_users.length == 0 && !parent_comment) ? 'comment-informed':'']" 
    :id="idComment">
    <div class="comment__replied" v-if="parent_comment" >
      <div class="comment__replied-user">
        {{ userNameId(parent_comment.creator_user_id) }}
      </div>
      <div class="comment__replied-comment" @click="$emit('scrollToRepliedComment', parent_comment.id)">
        {{ clearTagComment(parent_comment.comment) }}
      </div>
    </div>

    <div class="comment__body">
      <div class="comment__head">
        <span class="comment__username mr-2">
          <b-avatar
            class="mr-2"
            variant="secondary"
            :src="avatarPath"
            :text="nameAvatar(userNameId(userId))"
          />
          <div>
            <div>
              {{
                isCurrentUser
                  ? `${userNameId(userId)} (Вы)`
                  : userNameId(userId)
              }}

              <span v-if="isCanMarkMessages" class="comment__bookmarked">
                <span
                  v-b-tooltip.hover
                  title="Добавить в избранное"
                  v-if="!bookmarked"
                  @click="makeBookmarked(true)"
                >
                  <b-icon-bookmark-star class="comment__icon" />
                </span>
                <span
                  v-b-tooltip.hover
                  title="Убрать из избранного"
                  v-if="bookmarked"
                  @click="makeBookmarked(false)"
                >
                  <b-icon-bookmark-star-fill class="comment__icon" />
                </span>
              </span>
            </div>
            <div class="text-muted comment__date">{{ createdAt }}</div>
          </div>
        </span>

        <div class="comment__buttons ml-auto">
          <span
            v-if="isReplyMethod"
            class="d-flex"
            v-b-tooltip.hover
            title="Ответить"
            @click="$emit('replyComment')"
          >
            <b-icon-reply-fill class="comment__icon" />
          </span>
          <span
            v-if="isEditMethod && canModify"
            class="d-flex"
            v-b-tooltip.hover
            title="Изменить"
            @click="$emit('editComment')"
          >
            <b-icon-pencil-fill class="comment__icon" />
          </span>
          <span
            v-if="isRemoveMethod && canModify"
            class="d-flex"
            v-b-tooltip.hover
            title="Удалить"
            @click="remove"
          >
            <b-icon-x-lg class="comment__icon" />
          </span>
        </div>
      </div>

      <div>
        <span class="comment__text-comment" v-html="textComment" />
        <div 
          class="text-right text-muted comment__was-informed" 
          v-if="informed_users.length > 0"
        >
          Уведомлены: {{ informedString(informed_users) }}
        </div>
        <b-list-group v-if="files.length > 0" class="mt-1">
          <b-list-group-item
            v-for="(file, index) in files"
            :key="index"
            :action="true"
            class="d-flex justify-content-between align-items-center p-2"
          >
            {{ file.name }}

            <div class="d-flex align-items-center ml-auto">
              <div class="list-block_action ml-3">
                <a :href="hfs + file.path" download class="list-block_icon cursor-pointer p-1">
                  <i class="fas fa-download"></i>
                </a>
              </div>
              <div class="list-block_action ml-3">
                <span class="list-block_icon cursor-pointer p-1" @click="openFile(file)">
                    <i class="far fa-eye"></i>
                </span>
              </div>
            </div>
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { mixinTasks } from '@/mixins';
import { HOST_FOR_STORAGE } from '@/constants';

export default {
  name: 'Comment',

  props: {
    idComment: {
      type: Number,
    },
    idParentComment: {
      type: Number,
    },
    parent_comment: {
      type: Object,
      default: () => {},
    },
    userId: {
      type: Number,
    },
    avatarPath: {
      type: String,
      default: '',
    },
    createdAt: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    bookmarked: {
      type: Boolean | Number,
      default: false,
    },
    children: {
      type: Array,
      default: () => [],
    },
    files: {
      type: Array,
      default: () => [],
    },
    informed_users: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isCanMarkMessages: {
      type: Boolean,
      default: false,
    },
    // Сколько секунд у автора осталось на редактирование/удаление (считает сервер).
    // Передаётся только из чатов заказа и компании, в остальных чатах — 0.
    authorEditSecondsLeft: {
      type: Number,
      default: 0,
    },
  },

  mixins: [mixinTasks],

  data() {
    return {
      hfs: HOST_FOR_STORAGE,
      authorEditDeadline: 0,
      now: Date.now(),
      authorEditTimer: null,
    };
  },

  watch: {
    authorEditSecondsLeft: {
      handler(seconds) {
        this.stopAuthorEditTimer();
        this.now = Date.now();
        this.authorEditDeadline = seconds > 0 ? this.now + seconds * 1000 : 0;

        if (this.authorEditDeadline && this.isCurrentUser) {
          this.authorEditTimer = setInterval(() => {
            this.now = Date.now();
            if (this.now >= this.authorEditDeadline) {
              this.stopAuthorEditTimer();
            }
          }, 5000);
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this.stopAuthorEditTimer();
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),

    // names of emits - editComment, replyComment, remove
    isEditMethod() {
      return this.$listeners.editComment;
    },

    isReplyMethod() {
      return this.$listeners.replyComment;
    },

    isRemoveMethod() {
      return this.$listeners.remove;
    },

    textComment() {
      return this.text;
    },

    isCurrentUser() {
      return this.userId === this.currentUser.id;
    },

    isAdmin() {
      return Boolean(this.currentUser.role && this.currentUser.role.includes('administrator'));
    },

    isAuthorEditAllowed() {
      return this.isCurrentUser
        && this.authorEditDeadline > 0
        && this.now < this.authorEditDeadline;
    },

    // Админ — всегда, автор — в течение отведённого времени после публикации
    canModify() {
      return this.isAdmin || this.isAuthorEditAllowed;
    },

    isHasReply() {
      return this.$listeners && this.$listeners.replyComment;
    },
  },

  methods: {
    ...mapActions('fileViewer', ['viewFile']),

    stopAuthorEditTimer() {
      if (this.authorEditTimer) {
        clearInterval(this.authorEditTimer);
        this.authorEditTimer = null;
      }
    },

    openFile(item) {
      if ( this.fileExtension(item.path) !== "txt") {
          this.viewFile({name: item.name, src: this.hfs + item.path})
      } else {
          window.open(this.hfs + item.path, '_blank');
      }
    },

    clearTagComment(comment) {
      return comment.replace(/<\/?[^>]+(>|$)/gi, " ");
    },

    remove() {
      this.$emit('remove', [
        this.idComment,
        ...this.findChildrenId(this.children),
      ]);
    },

    makeBookmarked(bookmarked) {
      this.$emit('makeBookmarked', {
        id: this.idComment,
        bookmarked: bookmarked
      });
    },

    findChildrenId(array, id = []) {
      let ids = id;
      array.forEach((item) => {
        ids.push(item.id);
        if (item.children) {
          this.findChildrenId(item.children, ids);
        }
      });
      return ids;
    },

    informedString(informed_users) {
      return informed_users.map(u => u.user.name).join(', ');
    }
  },
};
</script>

<style lang="scss">
.comment {
  $self: &;
  position: relative;
  padding: 5px 10px;
  background: #fff;
  border-radius: 10px;

  &:hover {
    #{$self}__buttons {
      display: inline-flex;
    }
  }

  &__replied {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    font-size: 0.9rem;
    padding-left: 60px;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 18px;
      width: 35px;
      height: 18px;
      border-left: 2px solid #b8b8b8;
      border-bottom: 0 solid #b8b8b8;
      border-right: 0 solid #b8b8b8;
      border-top: 2px solid #b8b8b8;
      border-top-left-radius: 6px;
    }

    &-user {
      font-weight: bold;
    }

    &-comment {
      color: #7e8286;
      margin-left: 10px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;

      &:hover {
        color: #000;
        font-weight: bold;
      }
    }
  }

  &__head {
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    min-height: 25px;
    margin-bottom: 8px;
    margin-right: 70px;
  }

  &__icon {
    fill: #ea7b27;
    width: 15px;
    height: 15px;
    padding: 7px;
    cursor: pointer;
    box-sizing: content-box;
  }

  &__buttons {
    user-select: none;
    display: none;
    margin-left: 10px;
    position: absolute;
    right: 20px;
    top: -10px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0px 2px 1px 1px #dddddd;

    #{$self}__icon {
      &:hover,
      &.active {
        fill: var(--green);
        border-radius: 5px;
      }
    }
  }

  &__bookmarked {
    #{$self}__icon {
      &:hover,
      &.active {
        fill: var(--green);
        border-radius: 5px;
      }
    }
  }

  &__username {
    font-weight: 700;
    display: flex;
  }

  &__date {
    font-size: 0.6rem;
  }

  &__reply {
    color: #ea7b27;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }

  &__hidden {
    display: flex;
    align-items: center;
    width: 100%;
    background: #ea7b274f;
    border-radius: 5px;
    margin-top: 10px;
    cursor: pointer;
  }

  &__text-comment {
    white-space: pre-line;
    display: block;
    overflow: auto;
  }

  &__button {
    background-color: #7e8286;
    color: #fff;
    border: none;
    padding: 5px 15px;
    font-size: 0.9rem;
    border-radius: 5px;
    transition: 0.3s;
    cursor: pointer;

    &.disabled {
      opacity: 0.8;
      pointer-events: none;
    }

    &:hover {
      background: #ea7b27;
    }
  }

  &__was-informed {
    font-size: 0.6rem;
    color: #6c757d;
    padding-top: 5px;
    padding-bottom: 5px;
  }
}
.comment-informed {
  background: #e9e9e9;
}
</style>
